import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { useMemo, useState } from "react"
import { useNavigate } from "react-router-dom"
import {
  Bar,
  BarChart,
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts"
import {
  getAgentClients,
  getAgentInteractions,
  getAgentPerformance,
  getAgentPerformanceTrend,
  getAgentTargets,
} from "../api/endpoints/agents"
import { updateTarget } from "../api/endpoints/targets"
import { formatCurrency, formatDate, formatDateTime, formatEnumLabel } from "../utils/formatting"
import { CapacityGauge } from "./capacity_gauge"
import { ClientDetailDrawer } from "./client_detail_drawer"
import { PerformanceBandBadge } from "./performance_band_badge"
import { PipelineMiniBar } from "./pipeline_mini_bar"
import { TrendSparkline } from "./trend_sparkline"
import {
  Button,
  DataTable,
  Drawer,
  EmptyState,
  FormField,
  Input,
  PageSection,
  StatusPill,
  TableBody,
  TableHead,
  TableRow,
  Td,
  Th,
} from "./ui"

const TABS = [
  { key: "scorecard", label: "Scorecard" },
  { key: "trend", label: "Trend" },
  { key: "clients", label: "Clients" },
  { key: "interactions", label: "Interactions" },
  { key: "targets", label: "Targets" },
] as const

function scorecardTone(value: number, target?: number | null) {
  if (!target || target <= 0) {
    return "neutral"
  }

  const ratio = value / target
  if (ratio >= 1) return "success"
  if (ratio >= 0.7) return "warning"
  return "danger"
}

function buildHeatmap(rows: Array<{ activityDate: string; interactionCount: number }>) {
  const map = new Map(rows.map((row) => [row.activityDate, row.interactionCount]))
  const keys = rows.map((row) => row.activityDate).sort()
  if (keys.length === 0) {
    return []
  }

  const start = new Date(keys[0]!)
  const end = new Date(keys[keys.length - 1]!)
  const cells: Array<{ date: string; count: number }> = []
  const cursor = new Date(start)

  while (cursor.getTime() <= end.getTime()) {
    const date = cursor.toISOString().slice(0, 10)
    cells.push({ date, count: map.get(date) ?? 0 })
    cursor.setUTCDate(cursor.getUTCDate() + 1)
  }

  return cells
}

function heatTone(count: number) {
  if (count >= 8) return "bg-slate-900"
  if (count >= 5) return "bg-slate-700"
  if (count >= 3) return "bg-slate-500"
  if (count >= 1) return "bg-slate-300"
  return "bg-slate-100"
}

function clientSelectionSummary(clients: any[], selectedIds: string[]) {
  const selected = clients.filter((row) => selectedIds.includes(row.clientId))
  return {
    count: selected.length,
    outstanding: selected.reduce((sum, row) => sum + (row.outstandingBalance ?? 0), 0),
  }
}

export function AgentPerformanceDrawer({
  agentId,
  open,
  canManage,
  onClose,
}: {
  agentId: string | null
  open: boolean
  canManage: boolean
  onClose: () => void
}) {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [activeTab, setActiveTab] =
    useState<(typeof TABS)[number]["key"]>("scorecard")
  const [selectedClientIds, setSelectedClientIds] = useState<string[]>([])
  const [targetPercentage, setTargetPercentage] = useState("")
  const [targetReason, setTargetReason] = useState("")
  const [selectedClientId, setSelectedClientId] = useState<string | null>(null)

  const performanceQuery = useQuery({
    queryKey: ["admin-agent-performance", agentId],
    queryFn: () => getAgentPerformance(agentId!),
    enabled: open && Boolean(agentId),
  })

  const trendQuery = useQuery({
    queryKey: ["admin-agent-performance-trend", agentId],
    queryFn: () => getAgentPerformanceTrend(agentId!, { periods: "6" }),
    enabled: open && Boolean(agentId),
  })

  const clientsQuery = useQuery({
    queryKey: ["admin-agent-clients", agentId],
    queryFn: () =>
      getAgentClients(agentId!, {
        per_page: "50",
        sort_by: "days_overdue",
        sort_order: "desc",
      }),
    enabled: open && Boolean(agentId) && activeTab === "clients",
  })

  const interactionsQuery = useQuery({
    queryKey: ["admin-agent-interactions", agentId],
    queryFn: () =>
      getAgentInteractions(agentId!, {
        per_page: "25",
        sort_by: "created_at",
        sort_order: "desc",
      }),
    enabled: open && Boolean(agentId) && activeTab === "interactions",
  })

  const targetsQuery = useQuery({
    queryKey: ["admin-agent-targets", agentId],
    queryFn: () => getAgentTargets(agentId!),
    enabled: open && Boolean(agentId) && activeTab === "targets",
  })

  const performance = performanceQuery.data?.data
  const trend = trendQuery.data?.data?.periods ?? []
  const clients = clientsQuery.data?.data?.rows ?? []
  const interactions = interactionsQuery.data?.data?.rows ?? []
  const targets = targetsQuery.data?.data ?? []
  const selectedSummary = clientSelectionSummary(clients, selectedClientIds)
  const heatmapCells = buildHeatmap(performance?.activityHeatmap ?? [])

  const targetMutation = useMutation({
    mutationFn: () =>
      updateTarget(performance?.activeTarget?.targetId, {
        targetPercentage: Number(targetPercentage),
        notes: `Adjusted from Agent Performance: ${targetReason}`,
      }),
    onSuccess: async () => {
      setTargetPercentage("")
      setTargetReason("")
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["admin-agent-performance", agentId] }),
        queryClient.invalidateQueries({ queryKey: ["admin-agent-performance-trend", agentId] }),
        queryClient.invalidateQueries({ queryKey: ["admin-agent-targets", agentId] }),
        queryClient.invalidateQueries({ queryKey: ["performance-team-summary"] }),
        queryClient.invalidateQueries({ queryKey: ["performance-leaderboard"] }),
      ])
    },
  })

  const currentTarget = useMemo(
    () => targets.find((row: any) => row.isActive) ?? null,
    [targets],
  )

  function toggleClient(clientIdValue: string) {
    setSelectedClientIds((current) =>
      current.includes(clientIdValue)
        ? current.filter((entry) => entry !== clientIdValue)
        : [...current, clientIdValue],
    )
  }

  function routeToAssignments(withSelection: boolean) {
    if (!agentId) {
      return
    }

    const params = new URLSearchParams({
      tab: "assignments",
      mode: "reassign",
      source_agent_id: agentId,
    })

    if (withSelection && selectedClientIds.length > 0) {
      params.set("client_ids", selectedClientIds.join(","))
    }

    navigate(`/targets?${params.toString()}`)
    onClose()
  }

  return (
    <>
      <Drawer title="Agent Performance" open={open} onClose={onClose}>
        {performanceQuery.isLoading ? (
          <div className="space-y-4">
            <div className="h-8 w-48 animate-pulse rounded bg-slate-100" />
            <div className="h-40 animate-pulse rounded-xl bg-slate-100" />
            <div className="h-64 animate-pulse rounded-xl bg-slate-100" />
          </div>
        ) : !performance ? (
          <EmptyState title="Agent not found" description="No performance record was returned." />
        ) : (
          <div className="space-y-5 pb-24">
            <section className="rounded-2xl border border-slate-200 bg-slate-50 p-5">
              <div className="flex flex-wrap items-start justify-between gap-4">
                <div>
                  <p className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">
                    Agent
                  </p>
                  <h3 className="mt-2 text-2xl font-bold text-slate-900">
                    {performance.agentName}
                  </h3>
                  <div className="mt-2 flex flex-wrap items-center gap-2 text-sm text-slate-500">
                    <span>{performance.teamName ?? "No team"}</span>
                    <span>&middot;</span>
                    <span>{performance.accountStatus}</span>
                    <span>&middot;</span>
                    <span>Last active {formatDateTime(performance.lastSeenAt)}</span>
                  </div>
                </div>
                <div className="space-y-3">
                  <PerformanceBandBadge band={performance.performanceBand} />
                  <CapacityGauge
                    assignedClients={performance.assignedClients ?? 0}
                    remainingCapacity={performance.remainingCapacity ?? 0}
                    status={performance.capacityStatus}
                  />
                </div>
              </div>
            </section>

            <div className="inline-flex rounded-xl border border-slate-200 bg-white p-1">
              {TABS.map((tab) => (
                <button
                  key={tab.key}
                  type="button"
                  className={`rounded-lg px-3 py-2 text-sm font-medium ${
                    activeTab === tab.key
                      ? "bg-slate-900 text-white"
                      : "text-slate-600 hover:bg-slate-100"
                  }`}
                  onClick={() => setActiveTab(tab.key)}
                >
                  {tab.label}
                </button>
              ))}
            </div>

            {activeTab === "scorecard" ? (
              <div className="space-y-5">
                <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
                  {[
                    {
                      label: "Recovery Rate",
                      value: `${(performance.scorecard?.recoveryRate ?? 0).toFixed(1)}%`,
                      helper: `Target ${(performance.scorecard?.targetPercentage ?? 0).toFixed(1)}%`,
                    },
                    {
                      label: "Recovered / Outstanding",
                      value: formatCurrency(performance.scorecard?.recoveredAmount ?? 0),
                      helper: formatCurrency(performance.scorecard?.outstandingAmount ?? 0),
                    },
                    {
                      label: "Clients Resolved",
                      value: performance.scorecard?.clientsResolved ?? 0,
                      helper: `${performance.scorecard?.assignedClients ?? 0} assigned`,
                    },
                    {
                      label: "Interactions",
                      value: performance.scorecard?.interactionsLogged ?? 0,
                      helper: "Current period",
                    },
                    {
                      label: "Unprocessed",
                      value: performance.scorecard?.unprocessedCount ?? 0,
                      helper: `Longest ${formatDateTime(performance.lastSeenAt)}`,
                    },
                    {
                      label: "PTP Conversion",
                      value: `${(performance.scorecard?.ptpConversionRate ?? 0).toFixed(1)}%`,
                      helper: "Honoured vs broken",
                    },
                  ].map((card) => (
                    <div key={card.label} className="rounded-xl border border-slate-200 bg-white p-4">
                      <p className="text-xs uppercase tracking-[0.18em] text-slate-500">{card.label}</p>
                      <p className="mt-3 text-2xl font-bold text-slate-900">{card.value}</p>
                      <p className="mt-1 text-xs text-slate-500">{card.helper}</p>
                    </div>
                  ))}
                </div>

                <PageSection title="Pipeline Distribution">
                  <PipelineMiniBar values={performance.pipeline ?? {}} height="h-4" />
                </PageSection>

                <PageSection title="Daily Activity Heatmap">
                  {heatmapCells.length === 0 ? (
                    <EmptyState
                      title="No activity"
                      description="Daily interaction density will appear once activity is logged."
                    />
                  ) : (
                    <div className="grid grid-cols-7 gap-2">
                      {heatmapCells.map((cell) => (
                        <div
                          key={cell.date}
                          className={`aspect-square rounded-md ${heatTone(cell.count)}`}
                          title={`${cell.date}: ${cell.count} interactions`}
                        />
                      ))}
                    </div>
                  )}
                </PageSection>
              </div>
            ) : null}

            {activeTab === "trend" ? (
              <div className="space-y-5">
                <PageSection title="Period-over-Period Trend">
                  {trendQuery.isLoading ? (
                    <div className="h-72 animate-pulse rounded-xl bg-slate-100" />
                  ) : trend.length === 0 ? (
                    <EmptyState title="No trend history" description="Daily rollups have not populated yet." />
                  ) : (
                    <div className="h-72">
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={trend}>
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis dataKey="periodKey" />
                          <YAxis />
                          <Tooltip />
                          <Bar dataKey="achievedPercentage" fill="#2563eb" name="Achieved %" />
                          <Line
                            type="monotone"
                            dataKey="targetPercentage"
                            stroke="#dc2626"
                            strokeWidth={2}
                            name="Target %"
                          />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  )}
                </PageSection>

                <PageSection title="Rolling Metrics">
                  {trend.length === 0 ? (
                    <EmptyState title="No trend rows" description="Trend rows will appear once metrics exist." />
                  ) : (
                    <div className="overflow-x-auto">
                      <DataTable>
                        <TableHead>
                          <Th>Period</Th>
                          <Th>Target</Th>
                          <Th>Achieved</Th>
                          <Th>Gap</Th>
                          <Th>Interactions</Th>
                          <Th>Resolved</Th>
                          <Th>Rank</Th>
                        </TableHead>
                        <TableBody>
                          {trend.map((row: any) => (
                            <TableRow key={row.periodKey}>
                              <Td>{row.periodKey}</Td>
                              <Td>{row.targetPercentage?.toFixed(1) ?? "-"}%</Td>
                              <Td>{row.achievedPercentage?.toFixed(1) ?? "-"}%</Td>
                              <Td
                                className={
                                  (row.gapToTarget ?? 0) >= 0 ? "text-emerald-700" : "text-red-700"
                                }
                              >
                                {(row.gapToTarget ?? 0) >= 0 ? "+" : ""}
                                {(row.gapToTarget ?? 0).toFixed(1)}%
                              </Td>
                              <Td>{row.interactionsLogged ?? 0}</Td>
                              <Td>{row.clientsResolved ?? 0}</Td>
                              <Td>#{row.rank ?? "-"}</Td>
                            </TableRow>
                          ))}
                        </TableBody>
                      </DataTable>
                    </div>
                  )}
                </PageSection>
              </div>
            ) : null}

            {activeTab === "clients" ? (
              <div className="space-y-4">
                <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-slate-200 bg-slate-50 p-4">
                  <div>
                    <p className="text-sm font-semibold text-slate-900">
                      {selectedSummary.count} clients selected
                    </p>
                    <p className="text-xs text-slate-500">
                      {formatCurrency(selectedSummary.outstanding)} outstanding
                    </p>
                  </div>
                  {canManage ? (
                    <Button
                      onClick={() => routeToAssignments(true)}
                      disabled={selectedSummary.count === 0}
                    >
                      Reassign Selected
                    </Button>
                  ) : null}
                </div>

                {clientsQuery.isLoading ? (
                  <div className="h-72 animate-pulse rounded-xl bg-slate-100" />
                ) : clients.length === 0 ? (
                  <EmptyState title="No assigned clients" description="This agent has no assigned clients in scope." />
                ) : (
                  <div className="overflow-x-auto">
                    <DataTable>
                      <TableHead>
                        <Th />
                        <Th>Client</Th>
                        <Th>Loan</Th>
                        <Th>Outstanding</Th>
                        <Th>Category</Th>
                        <Th>Days Overdue</Th>
                        <Th>Pipeline</Th>
                        <Th>Last Contact</Th>
                      </TableHead>
                      <TableBody>
                        {clients.map((row: any) => (
                          <TableRow key={row.clientId}>
                            <Td>
                              <input
                                checked={selectedClientIds.includes(row.clientId)}
                                type="checkbox"
                                onChange={() => toggleClient(row.clientId)}
                              />
                            </Td>
                            <Td>
                              <button
                                type="button"
                                className="text-left"
                                onClick={() => setSelectedClientId(row.clientId)}
                              >
                                <span className="font-semibold text-slate-900">{row.clientName}</span>
                                <span className="block text-xs text-slate-500">{row.clientId}</span>
                              </button>
                            </Td>
                            <Td>{row.loanId}</Td>
                            <Td>{formatCurrency(row.outstandingBalance ?? 0, row.currency)}</Td>
                            <Td>{formatEnumLabel(row.overdueCategory)}</Td>
                            <Td>{row.daysOverdue ?? 0}</Td>
                            <Td>{formatEnumLabel(row.pipelineStatus)}</Td>
                            <Td>{formatDateTime(row.lastContactAt)}</Td>
                          </TableRow>
                        ))}
                      </TableBody>
                    </DataTable>
                  </div>
                )}
              </div>
            ) : null}

            {activeTab === "interactions" ? (
              <PageSection title="Recent Interactions">
                {interactionsQuery.isLoading ? (
                  <div className="h-72 animate-pulse rounded-xl bg-slate-100" />
                ) : interactions.length === 0 ? (
                  <EmptyState title="No interactions" description="No interaction log entries were returned." />
                ) : (
                  <div className="overflow-x-auto">
                    <DataTable>
                      <TableHead>
                        <Th>Timestamp</Th>
                        <Th>Client</Th>
                        <Th>Channel</Th>
                        <Th>Outcome</Th>
                        <Th>Status Change</Th>
                        <Th>Notes</Th>
                      </TableHead>
                      <TableBody>
                        {interactions.map((row: any) => (
                          <TableRow key={row.interactionId}>
                            <Td>{formatDateTime(row.createdAt)}</Td>
                            <Td>{row.clientName}</Td>
                            <Td>{formatEnumLabel(row.interactionType)}</Td>
                            <Td>{formatEnumLabel(row.outcome)}</Td>
                            <Td>
                              {row.toPipelineStatus
                                ? `${formatEnumLabel(row.fromPipelineStatus)} -> ${formatEnumLabel(row.toPipelineStatus)}`
                                : "-"}
                            </Td>
                            <Td>{row.notes ?? "-"}</Td>
                          </TableRow>
                        ))}
                      </TableBody>
                    </DataTable>
                  </div>
                )}
              </PageSection>
            ) : null}

            {activeTab === "targets" ? (
              <div className="space-y-5">
                {canManage && performance.activeTarget ? (
                  <PageSection title="Adjust Current Target">
                    <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                      <FormField label="New target percentage">
                        <Input
                          type="number"
                          value={targetPercentage}
                          onChange={(event) => setTargetPercentage(event.target.value)}
                        />
                      </FormField>
                      <FormField label="Reason">
                        <Input
                          value={targetReason}
                          onChange={(event) => setTargetReason(event.target.value)}
                        />
                      </FormField>
                    </div>
                    <div className="mt-4">
                      <Button
                        onClick={() => targetMutation.mutate()}
                        disabled={
                          targetMutation.isPending ||
                          !targetPercentage.trim() ||
                          !targetReason.trim() ||
                          !performance.activeTarget?.targetId
                        }
                      >
                        Adjust Target
                      </Button>
                    </div>
                  </PageSection>
                ) : null}

                <PageSection title="Target History">
                  {targetsQuery.isLoading ? (
                    <div className="h-72 animate-pulse rounded-xl bg-slate-100" />
                  ) : targets.length === 0 ? (
                    <EmptyState title="No targets" description="No targets are linked to this agent yet." />
                  ) : (
                    <div className="overflow-x-auto">
                      <DataTable>
                        <TableHead>
                          <Th>Target</Th>
                          <Th>Period</Th>
                          <Th>Metric</Th>
                          <Th>Status</Th>
                          <Th>Set By</Th>
                          <Th>Updated</Th>
                        </TableHead>
                        <TableBody>
                          {targets.map((row: any) => (
                            <TableRow key={row.publicId}>
                              <Td>
                                <div className="font-semibold text-slate-900">{row.name}</div>
                                <div className="text-xs text-slate-500">{row.scopeType}</div>
                              </Td>
                              <Td>
                                {row.timeframeStart && row.timeframeEnd
                                  ? `${formatDate(row.timeframeStart)} - ${formatDate(row.timeframeEnd)}`
                                  : row.timeframe}
                              </Td>
                              <Td>{row.targetPercentage?.toFixed(1)}%</Td>
                              <Td>
                                <StatusPill tone={row.isActive ? "success" : "neutral"}>
                                  {row.isActive ? "In Progress" : "Inactive"}
                                </StatusPill>
                              </Td>
                              <Td>{row.setByName ?? "-"}</Td>
                              <Td>{formatDateTime(row.updatedAt)}</Td>
                            </TableRow>
                          ))}
                        </TableBody>
                      </DataTable>
                    </div>
                  )}
                </PageSection>
              </div>
            ) : null}
          </div>
        )}

        {performance ? (
          <div className="sticky bottom-0 mt-6 flex flex-wrap items-center justify-between gap-3 border-t border-slate-200 bg-white/95 px-0 pt-4 backdrop-blur">
            <div className="text-sm text-slate-500">
              Current recovery rate{" "}
              <span
                className={`font-semibold ${
                  scorecardTone(performance.recoveryRate ?? 0, performance.targetPercentage) ===
                  "success"
                    ? "text-emerald-700"
                    : scorecardTone(performance.recoveryRate ?? 0, performance.targetPercentage) ===
                        "warning"
                      ? "text-amber-700"
                      : "text-red-700"
                }`}
              >
                {(performance.recoveryRate ?? 0).toFixed(1)}%
              </span>
            </div>
            <div className="flex flex-wrap gap-2">
              <Button variant="secondary" onClick={() => routeToAssignments(false)}>
                Reassign Clients
              </Button>
              {canManage ? (
                <Button variant="outline" onClick={() => setActiveTab("targets")}>
                  Adjust Target
                </Button>
              ) : null}
            </div>
          </div>
        ) : null}
      </Drawer>

      <ClientDetailDrawer
        clientId={selectedClientId}
        open={Boolean(selectedClientId)}
        onClose={() => setSelectedClientId(null)}
      />
    </>
  )
}
